import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, RefreshCw, Factory, Loader2, CheckCircle2, Clock,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { useAuth } from '@/lib/auth'

type Item = {
  op_id: number; op_numero: number
  produto_codigo: string | null; produto_descricao: string
  variante: string; quantidade: string; quantidade_produzida: string
  etapa_op_id: number; etapa_codigo: string; etapa_nome: string
  status: string; op_status: string
  iniciada_em: string | null; pausada_em: string | null
  qr_token: string | null; fim_previsto: string | null
}

type FilaMaquina = {
  maquina: { id: number; codigo: string; nome: string; tipo: string; capacidade_hora_kg: number }
  operador: { id: number; nome: string }
  items: Item[]
}

export default function MobileMaquinaFilaPage() {
  const { token: maqToken } = useParams<{ token: string }>()
  const nav = useNavigate()
  const { token } = useAuth()
  const [fila, setFila] = useState<FilaMaquina | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!token) {
      // Salva URL pra redirecionar depois do login
      sessionStorage.setItem('m_redirect', `/m/maquina/${maqToken}`)
      nav('/m/login', { replace: true })
    }
  }, [token])

  async function refresh() {
    try {
      const r = await api.get<FilaMaquina>(`/m/maquina/${maqToken}`)
      setFila(r.data)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Máquina não encontrada')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { refresh() }, [maqToken])
  useEffect(() => {
    const iv = setInterval(refresh, 30_000)
    return () => clearInterval(iv)
  }, [maqToken])

  async function abrirOP(item: Item) {
    if (item.qr_token) {
      nav(`/m/op/${item.qr_token}`)
    } else {
      try {
        const r = await api.post(`/industrial/ordens/${item.op_id}/qr-token`)
        nav(`/m/op/${r.data.token}`)
      } catch (e: any) {
        toast.error(e?.response?.data?.detail || 'Erro')
      }
    }
  }

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-ink-50">
      <Loader2 className="h-12 w-12 animate-spin text-violet-600" />
    </div>
  }

  if (!fila) return null

  return (
    <div className="min-h-screen bg-ink-50 pb-24">
      <header className="bg-gradient-to-br from-ink-900 to-ink-800 text-white p-4 sticky top-0 z-10 shadow-lg">
        <div className="flex items-center gap-3">
          <button onClick={() => nav('/m/operador')} className="p-2 -ml-2 rounded-lg active:bg-white/10">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="w-10 h-10 rounded-lg bg-violet-600 flex items-center justify-center shrink-0">
            <Factory className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs opacity-70">Máquina</div>
            <div className="font-extrabold text-lg truncate">{fila.maquina.codigo}</div>
          </div>
          <button onClick={refresh} className="p-2 rounded-lg bg-white/15 hover:bg-white/25">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-2 text-xs opacity-70 truncate">
          {fila.maquina.nome} · {fila.maquina.tipo} · {fila.maquina.capacidade_hora_kg} kg/h
        </div>
      </header>

      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between px-1">
          <h2 className="text-sm font-bold text-ink-900">Fila pendente nesta máquina</h2>
          <span className="text-xs text-ink-500">{fila.items.length}</span>
        </div>

        {fila.items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-ink-200 p-8 text-center">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-2" />
            <div className="font-bold text-ink-900">Sem OPs pendentes</div>
            <div className="text-xs text-ink-500 mt-1">Esta máquina está livre</div>
          </div>
        ) : (
          fila.items.map((it, idx) => {
            const pausada = !!it.pausada_em
            const cor = pausada ? 'border-amber-400 bg-amber-50' :
              it.status === 'em_execucao' ? 'border-emerald-400 bg-emerald-50' :
              'border-ink-200 bg-white'
            const statusLabel = pausada ? '⏸ pausada' :
              it.status === 'em_execucao' ? '▶ em execução' : '⏳ pendente'
            const statusCor = pausada ? 'bg-amber-200 text-amber-900' :
              it.status === 'em_execucao' ? 'bg-emerald-200 text-emerald-900' : 'bg-ink-200 text-ink-700'
            const atrasado = it.fim_previsto && new Date(it.fim_previsto) < new Date()

            return (
              <button
                key={it.etapa_op_id}
                onClick={() => abrirOP(it)}
                className={`w-full text-left rounded-2xl border-2 p-4 active:opacity-70 transition ${cor}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-ink-900 text-white text-xs font-bold flex items-center justify-center">
                      {idx + 1}
                    </span>
                    <span className="text-xs font-mono text-ink-500">OP #{it.op_numero}</span>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${statusCor}`}>
                    {statusLabel}
                  </span>
                </div>
                <div className="font-bold text-base text-ink-900 line-clamp-2">{it.produto_descricao}</div>
                {it.produto_codigo && (
                  <div className="text-xs font-mono text-ink-500 mt-0.5">{it.produto_codigo}</div>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-700">
                  <span className="font-semibold text-violet-700">📍 {it.etapa_nome}</span>
                  <span>📦 {Number(it.quantidade_produzida).toLocaleString('pt-BR')}/{Number(it.quantidade).toLocaleString('pt-BR')}</span>
                  {atrasado && <span className="text-red-700 font-bold">⚠ ATRASADA</span>}
                </div>
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}
